/**
 * ==========================================================================
 *
 * Scope: Foundation Phase
 *
 * Responsibility:
 * Allows designers to validate and cross-check assigned AI Widget Roles while creating
 * reusable AI Layout Templates.
 *
 * ==========================================================================
 */

import React, { useContext, useEffect, useRef, useState } from "react";
import { DropdownItem } from "reactstrap";
import cx from "classnames";

import useAIDesignAccessManager from "../../../hooks/useAIDesignAccessManager";
import { parseStrToDomNode, removeWhiteSpaceBetweenTags } from "../../../_helpers/utils";
import { EditorContext } from "../../../containers/editor/EditorLayout";
import { Input } from "../../ui/input";
import { Icon } from "../../ui/icon";
import global from "../../../scss/dhp.scss";

const style = Object.assign({}, global);

const ToggleWidgetRole = () => {
  const checkboxId = "toggle-widget-role-checkbox";
  const isActive = document.querySelectorAll(".widget-role-wrapper")?.length > 0;

  const hasAccessToWR = useAIDesignAccessManager({ component: "widget_role" });
  const { widgets, setDisableEditor, metadata, updateMetadata } = useContext(EditorContext);

  const isFirstRender = useRef(true);
  const [showWRoles, setShowWRoles] = useState(isActive);
  const [noWRoleFound, setNoWRoleFound] = useState(false);

  const toggleWRoles = () => {
    if (showWRoles) {
      // reset and show widget roles
      hideWidgetRoles();
      showWidgetRoles();

      // show widget role overlays
      showWidgetRoleOverlays();

      // disable rest of the editor
      setDisableEditor(true);

      // close editor left panel if opened
      toggleEditorLeftPanel("hide");

      // disable active widget (if any)
      updateMetadata({
        ...metadata,
        activeWidgetId: false,
        activeWidgetType: false,
      });
    } else {
      // hide widget roles
      hideWidgetRoles();

      // hide widget role overlays
      hideWidgetRoleOverlays();

      // enable rest of the editor
      setDisableEditor(false);

      // open editor left panel if closed
      toggleEditorLeftPanel("show");
    }
  };

  const showWidgetRoles = () => {
    // append all widget role nodes in DOM
    widgets?.forEach(widgetObj => {
      const hasWidgetRole = widgetObj?.AI?.widget_role;

      if (hasWidgetRole) {
        const widget = document.getElementById(widgetObj.id);
        const widgetRoleNode = parseStrToDomNode({
          htmlStr: removeWhiteSpaceBetweenTags(`
                  <div class="widget-role-wrapper">
                    ${hasWidgetRole?.name || ""}
                  </div>
                `),
          selector: "div",
        });
        widget.insertBefore(widgetRoleNode, widget.firstChild);
      }
    });

    // apply precise top tailored for each widget role wrapper
    document.querySelectorAll(".widget-role-wrapper").forEach(wrapper => {
      const widget = wrapper.closest(".dhp-root-widget");
      if (!widget) return;

      const widgetH = parseFloat(window.getComputedStyle(widget).height);
      const wrapperH = parseFloat(window.getComputedStyle(wrapper).height);
      const preciseTop = (widgetH - wrapperH) / 2;

      wrapper.style.setProperty("top", `${preciseTop}px`, "important");
    });
  };

  const hideWidgetRoles = () => {
    // remove all widget role nodes from DOM
    document.querySelectorAll(".widget-role-wrapper").forEach(elm => {
      elm.remove();
    });
  };

  const showWidgetRoleOverlays = () => {
    // append all widget role overlay nodes in DOM
    document.querySelectorAll(".dhp-page-block")?.forEach(block => {
      const widgetRoleOverlayNode = parseStrToDomNode({
        htmlStr: removeWhiteSpaceBetweenTags(`<div class="widget-role-overlay"></div>`),
        selector: "div",
      });
      block.insertBefore(widgetRoleOverlayNode, block.firstChild);
    });
  };

  const hideWidgetRoleOverlays = () => {
    // remove all widget role overlay nodes from DOM
    document.querySelectorAll(".widget-role-overlay").forEach(elm => {
      elm.remove();
    });
  };

  const toggleEditorLeftPanel = action => {
    const toggleButton = document.querySelector("#canvas-panel-area .panel-toggle");
    if (!toggleButton) return;

    const currentState = document.querySelector(".editor-body.hide-secondAside") ? "hidden" : "revealed";

    if (action === "hide" && currentState === "revealed") {
      toggleButton.click();
    }

    if (action === "show" && currentState === "hidden") {
      toggleButton.click();
    }
  };

  const simulateClickOnCheckbox = () => {
    const checkbox = document.getElementById(checkboxId);
    if (!checkbox) return;

    checkbox.click();
  };

  const toggleDropdownItem = e => {
    // discard: if click event triggered from checkbox
    if (e.target.id === checkboxId) return;

    // force click checkbox
    simulateClickOnCheckbox();
  };

  const toggleCheckbox = e => {
    const isChecked = Boolean(e.target.checked);

    // discard: if old and new value is same
    if (isChecked === showWRoles) return;

    setShowWRoles(prev => !prev);
  };

  useEffect(() => {
    // restrict if has no permission
    if (!hasAccessToWR) return;

    // handles mounting time behavior
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // show widget roles based on toggle status
    toggleWRoles();
  }, [showWRoles, hasAccessToWR]);

  useEffect(() => {
    const noWRoles = widgets?.filter(w => w?.AI?.widget_role)?.length === 0;
    setNoWRoleFound(noWRoles);
  }, []);

  return (
    <>
      {hasAccessToWR && (
        <div
          className={cx(
            style["toggle-widget-role"],
            { [style["disabled"]]: noWRoleFound },
            { [style["cursor-not-allowed"]]: noWRoleFound }
          )}>
          <hr />
          <DropdownItem
            tag="a"
            toggle={false}
            disabled={noWRoleFound}
            className={cx(style["justify-content-between"], style["mt-2"])}
            onClick={toggleDropdownItem}>
            <div className={cx(style["d-flex"], style["align-items-center"])}>
              <Icon icon={"ui-widget-role"} additionalclass={"me-2"} /> <span>Show Widget Role</span>
            </div>

            <div className={cx(style["publishTool"], style["ms-3"])}>
              <div className={cx(style["d-flex"], style["align-items-center"])}>
                <div
                  className={cx(style["form-check"], style["form-switch"], {
                    [style["disable"]]: false,
                  })}>
                  <Input
                    cssModule={style}
                    returnType="noGroup"
                    type="checkbox"
                    className={style["form-check-input"]}
                    id={checkboxId}
                    label=""
                    defaultChecked={isActive}
                    onChange={toggleCheckbox}
                    disabled={noWRoleFound}
                  />
                </div>
              </div>
            </div>
          </DropdownItem>
        </div>
      )}
    </>
  );
};

export default ToggleWidgetRole;
